import {isNumber} from 'lodash';
import React, {FC, useMemo, useState} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/components/trans';
import {getObjectValue, isDefined} from '../../../../libs/shared/core/src/helpers/typescript/typeHelpers';
import {globalNamespace} from '../../../../libs/shared/core/src/models/helpers/namespaceHelpers';
import {fetchApiAsync, postApiAsync, putApiAsync} from '../../../../libs/shared/core/src/services/http/http';
import {HttpResponseError} from '../../../../libs/shared/core/src/services/http/httpError';
import {Button} from '../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {AsyncActionButton} from '../../../../libs/shared/ui/src/components/buttons/loadingButton/asyncActionButton';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {useAppSelector} from '../../../helpers/appReduxHelpers';
import {ApiCircle, CircleModel} from '../../../models/api/circleModel';
import {DepartmentsEnum} from '../../../models/api/userMetadataModel';
import {getCompany} from '../../../store/data/company/companySelectors';
import {getUserId} from '../../../store/data/teammates/teammatesSelectors';
import {OnboardingSelectionButton} from './onboardingSelectionButton';
import {
  generateStepText,
  newOnboardingSlideHeight,
  newOnboardingSlideWidth,
  SlidesCommonProps,
  StyledFormDescriptionDiv,
  StyledOnboardingFormTitleDiv,
  StyledOnboardingFormTitlesDiv,
  StyledOnboardingNavSkipDiv,
  TeamsItems,
} from './onboardingWelcomeHelpers';
import {OnboardingWelcomeSlide} from './onboardingWelcomeSlide';

/*
 * Props.
 */

interface OnboardingTeammateGroupsSlideProps extends SlidesCommonProps {
  onNext: (circle: CircleModel | undefined) => void;
  onSkip?: () => void;
  onCancel?: () => void;
  createdCircle: CircleModel | undefined;
  selectedTeamType: DepartmentsEnum | string;
  setSelectedTeamType: (arg: DepartmentsEnum | string) => void;
  setHasError: (hasError: boolean) => void;
  onChangeDepartment: (arg: DepartmentsEnum) => void;
  onPrevious?: () => void;
  onCircleIdChange: (circleId: number) => void;
  otherTeam: string;
  setOtherTeam: (team: string) => void;
  isOtherTeamType: boolean;
  teamDisplayName: string;
  initialHasTeamType: boolean;
}

/*
 * Style.
 */

const StyledPreviousButtonDiv = styled.div`
  padding-right: 10px;
`;

const StyledLeftSlideContainerDiv = styled.div`
  flex: 0 0 103%;
`;

const StyledTeamsRadioContainerDiv = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  max-width: 470px;
`;

/*
 * Component.
 */

export const OnboardingTeammateGroupsSlide: FC<OnboardingTeammateGroupsSlideProps> = (props) => {
  const {
    stepNumber,
    totalStepCount,
    createdCircle,
    selectedTeamType,
    setSelectedTeamType,
    onCircleIdChange,
    otherTeam,
    setOtherTeam,
    setHasError,
    onPrevious,
    onSkip,
    onNext,
    onCancel,
    isOtherTeamType,
    teamDisplayName,
  } = props;

  const company = useAppSelector(getCompany);
  const userId = useAppSelector(getUserId);
  const [isSelectionDirty, setIsSelectionDirty] = useState(false);

  const createTeammateGroup = async () => {
    const circle = {
      name: teamDisplayName,
      description: '', // Should we prefill this?
      namespace: globalNamespace,
      teammate_ids: [userId],
      inbox_ids: [],
      team_ids: [],
    };

    try {
      const newCircle: CircleModel = await postApiAsync(
        ApiCircle,
        `${company.url}/namespaces/global/circles`,
        circle,
      );

      onCircleIdChange(newCircle.id);
      return newCircle;
    } catch (err) {
      const existingCircle = await maybeFetchConflictingCircle(company.url, err);
      onCircleIdChange(existingCircle.id);
      return existingCircle;
    }
  };

  const editTeammateGroup = async (currentCircle: CircleModel) => {
    const updatedCircled: CircleModel = {
      ...currentCircle,
      name: teamDisplayName,
    };

    try {
      const newCircle: CircleModel = await putApiAsync(ApiCircle, updatedCircled.url, updatedCircled);
      onCircleIdChange(newCircle.id);
      return newCircle;
    } catch (err) {
      const existingCircle = await maybeFetchConflictingCircle(company.url, err);
      onCircleIdChange(existingCircle.id);
      return existingCircle;
    }
  };

  const isNextDisabled = useMemo(
    () => (isOtherTeamType && !otherTeam) || !selectedTeamType,
    [isOtherTeamType, otherTeam, selectedTeamType],
  );

  const shouldShowPreviousButton = isDefined(onPrevious);
  const shouldShowSkipButton = isDefined(onSkip);
  const shouldShowCancelButton = isDefined(onCancel);

  return (
    <OnboardingWelcomeSlide
      width={newOnboardingSlideWidth}
      height={newOnboardingSlideHeight}
      navSecondary={generateStepText(stepNumber, totalStepCount)}
      navPrimary={
        <>
          {shouldShowSkipButton && (
            <StyledOnboardingNavSkipDiv>
              <Button
                variant={ButtonVariantsEnum.SECONDARY}
                onClick={() => {
                  if (isOtherTeamType && otherTeam.length === 0) {
                    setSelectedTeamType('');
                  }
                  onSkip?.();
                }}
              >
                <Trans id="onboardingwelcome-skip">Skip</Trans>
              </Button>
            </StyledOnboardingNavSkipDiv>
          )}
          {shouldShowCancelButton && (
            <StyledOnboardingNavSkipDiv>
              <Button variant={ButtonVariantsEnum.SECONDARY} onClick={() => onCancel?.()}>
                <Trans id="onboardingwelcome-cancel">Set up on my own</Trans>
              </Button>
            </StyledOnboardingNavSkipDiv>
          )}
          {shouldShowPreviousButton && (
            <StyledPreviousButtonDiv>
              <Button
                variant={ButtonVariantsEnum.SECONDARY}
                onClick={() => {
                  if (onPrevious) {
                    onPrevious();
                  }
                }}
              >
                <Trans id="onboardingwelcome-skip">Previous</Trans>
              </Button>
            </StyledPreviousButtonDiv>
          )}

          <AsyncActionButton
            isDisabled={isNextDisabled}
            variant={ButtonVariantsEnum.PRIMARY}
            interactionId="teammate_group_next"
            size={VisualSizesEnum.MEDIUM}
            onClick={async () => {
              // If we haven't changed our selection, no need to try to edit or create circle object.
              if (!isSelectionDirty && createdCircle) {
                onNext(createdCircle);
                return;
              }
              try {
                const newCircle = createdCircle
                  ? await editTeammateGroup(createdCircle)
                  : await createTeammateGroup();
                onNext(newCircle);
              } catch (e) {
                setHasError(true);
                onNext(undefined);
              }
            }}
          >
            <Trans id="onboardingwelcome-next">Next</Trans>
          </AsyncActionButton>
        </>
      }
      isLeftContent
    >
      <StyledLeftSlideContainerDiv>
        <StyledOnboardingFormTitlesDiv>
          <StyledOnboardingFormTitleDiv>
            <Trans id="q3-onboardingwelcome-teammate-groups-intro">It&rsquo;s time to set up Front! 🎉</Trans>
            <br />
            <Trans id="q3-onboardingwelcome-teammate-groups-title">First, tell us about your team</Trans>
          </StyledOnboardingFormTitleDiv>
        </StyledOnboardingFormTitlesDiv>
        <StyledFormDescriptionDiv>
          <Trans id="onboardingwelcome-teammate-groups-description">
            Here, you are creating a teammate group, which will allow you to grant multiple people access to a
            shared inbox. You can add teammates to this group later.
          </Trans>
        </StyledFormDescriptionDiv>
        <StyledTeamsRadioContainerDiv>
          {TeamsItems.map(({title, key}) => {
            const isOther = Boolean(key === DepartmentsEnum.OTHER);
            return (
              <OnboardingSelectionButton
                key={key}
                type="radio"
                isFreeform={isOther}
                value={otherTeam}
                onChangeValue={setOtherTeam}
                isChecked={Boolean(key === selectedTeamType)}
                onClick={() => {
                  if (!isOther) {
                    setOtherTeam('');
                  }
                  props.onChangeDepartment(key);
                  setSelectedTeamType(key);
                  setIsSelectionDirty(true);
                }}
                label={title}
              />
            );
          })}
        </StyledTeamsRadioContainerDiv>
      </StyledLeftSlideContainerDiv>
    </OnboardingWelcomeSlide>
  );
};

async function maybeFetchConflictingCircle(companyUrl: string, err: unknown) {
  if (err instanceof HttpResponseError && err.status === 409) {
    const conflictingId = getObjectValue(getObjectValue(err.body, 'conflicting'), 'id');
    if (isNumber(conflictingId)) {
      const existingCircle = await fetchApiAsync(ApiCircle, `${companyUrl}/circles/${conflictingId}`);
      return existingCircle;
    }
  }
  throw err;
}
